import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import {
  buildDesignersLeadBody,
  getDesignersAudience,
  getDesignersContact,
  getDesignersExamples,
  getDesignersFeatures,
  getDesignersFinalCta,
  getDesignersHero,
  getDesignersLeadTypes,
  getDesignersLogosStrip,
  getDesignersNav,
  getDesignersSeo,
  getDesignersSteps,
  getDesignersWholesale,
} from './designersLandingContent'
import {
  applyLandingSeo,
  buildRegisterPath,
  captureUtmParams,
  initLandingAnalytics,
  openLeadChannel,
  trackLandingCta,
  useLandingDarkThemeEffect,
} from './landingUtils'
import {
  LANDING_EXAMPLES,
  LANDING_EXAMPLE_COUNT,
  DESIGNERS_HERO_PREVIEW,
  LANDING_LIB_IMAGES,
} from './landingMedia'
import { PRODUCT_LOGO_SRC, PRODUCT_NAME } from '../../config/product'
import './LandingPage.css'

const DESIGNERS_CONTACT_EMAIL = 'inbox@spectrai.pro'

type LeadForm = {
  name: string
  contact: string
  type: string
  comment: string
}

export default function DesignersLandingPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [formSent, setFormSent] = useState(false)
  const [form, setForm] = useState<LeadForm>({
    name: '',
    contact: '',
    type: 'freelance',
    comment: '',
  })

  const designersNav = useMemo(() => getDesignersNav(), [t])
  const designersHero = useMemo(() => getDesignersHero(), [t])
  const designersAudience = useMemo(() => getDesignersAudience(), [t])
  const designersWholesale = useMemo(() => getDesignersWholesale(), [t])
  const designersFeatures = useMemo(() => getDesignersFeatures(), [t])
  const designersExamples = useMemo(() => getDesignersExamples(), [t])
  const designersSteps = useMemo(() => getDesignersSteps(), [t])
  const designersContact = useMemo(() => getDesignersContact(), [t])
  const designersFinalCta = useMemo(() => getDesignersFinalCta(), [t])
  const designersLogosStrip = useMemo(() => getDesignersLogosStrip(), [t])
  const designersLeadTypes = useMemo(() => getDesignersLeadTypes(), [t])

  const libImgs = LANDING_LIB_IMAGES

  useEffect(() => {
    captureUtmParams()
    initLandingAnalytics()
    const seo = getDesignersSeo()
    applyLandingSeo({
      page: 'designers',
      title: seo.title,
      description: seo.description,
      path: seo.path,
    })
  }, [t])

  useEffect(useLandingDarkThemeEffect(), [])

  useEffect(() => {
    if (!mobileMenuOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileMenuOpen])

  const goRegister = (source: string) => {
    trackLandingCta('designers', source)
    navigate(buildRegisterPath('designers'))
  }

  const scrollToContact = (source: string) => {
    trackLandingCta('designers', source)
    setMobileMenuOpen(false)
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleLeadSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!form.name.trim() || !form.contact.trim()) return
    trackLandingCta('designers', 'form_submit')
    const typeLabel =
      designersLeadTypes.find(item => item.value === form.type)?.label ?? form.type
    const body = buildDesignersLeadBody({
      name: form.name.trim(),
      contact: form.contact.trim(),
      typeLabel,
      comment: form.comment.trim() || undefined,
    })
    openLeadChannel(designersContact.mailSubject, body)
    setFormSent(true)
  }

  return (
    <div className="kmin bg-[#07070A] text-white min-h-screen font-sans overflow-hidden">
      <div className="kmin-glow">
        <div className="kmin-glow-top" />
        <div className="kmin-glow-bottom" />
      </div>

      <header className="kmin-nav">
        <div className="kmin-container">
          <div className="kmin-nav-inner">
            <img className="kmin-logo-img" src={PRODUCT_LOGO_SRC} alt={PRODUCT_NAME} />
            <nav className="kmin-nav-links" aria-label={t('landingDesigners.nav.aria')}>
              {designersNav.map(item => (
                <a key={item.href} href={item.href} onClick={() => setMobileMenuOpen(false)}>
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="kmin-nav-actions">
              <button
                type="button"
                className="kmin-nav-burger"
                aria-label={mobileMenuOpen ? t('landingDesigners.navMenu.closeMenu') : t('landingDesigners.navMenu.openMenu')}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(v => !v)}
              >
                <span className="material-symbols-outlined">
                  {mobileMenuOpen ? 'close' : 'menu'}
                </span>
              </button>
              <button
                type="button"
                className="kmin-btn-primary kmin-nav-cta"
                onClick={() => scrollToContact('nav_apply')}
              >
                {designersHero.primaryCta}
              </button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <>
            <button
              type="button"
              className="kmin-nav-mobile-backdrop"
              aria-label={t('landingDesigners.navMenu.closeMenu')}
              onClick={() => setMobileMenuOpen(false)}
            />
            <nav className="kmin-nav-mobile-sheet" aria-label={t('landingDesigners.navMenu.ariaMobile')}>
              {designersNav.map(item => (
                <a key={item.href} href={item.href} onClick={() => setMobileMenuOpen(false)}>
                  {item.label}
                </a>
              ))}
              <button
                type="button"
                className="kmin-btn-primary kmin-btn-block"
                onClick={() => scrollToContact('nav_mobile_apply')}
              >
                {designersHero.primaryCta}
              </button>
            </nav>
          </>
        )}
      </header>

      <section className="kmin-hero kmin-hero--designers">
        <div className="kmin-container">
          <div className="kmin-hero-grid">
            <div>
              <div className="kmin-badge">{designersHero.badge}</div>
              <h1 className="kmin-hero-title">
                <span style={{ color: '#ff3495' }}>{designersHero.titleAccent}</span>
                <span className="block">{designersHero.titleRest}</span>
              </h1>
              <p className="kmin-hero-desc">{designersHero.subtitle}</p>
              <div className="kmin-hero-actions">
                <button
                  type="button"
                  className="kmin-btn-primary kmin-btn-lg"
                  onClick={() => scrollToContact('hero_primary')}
                >
                  {designersHero.primaryCta}
                </button>
                <button
                  type="button"
                  className="kmin-btn-outline kmin-btn-lg"
                  onClick={() => goRegister('hero_secondary')}
                >
                  {designersHero.secondaryCta}
                </button>
              </div>
              <p className="kmin-hero-trial-badge">{designersHero.trialBadge}</p>
            </div>

            <div className="kmin-hero-preview">
              <div className="kmin-hero-preview-glow" />
              <div className="kmin-hero-preview-card">
                <div className="kmin-preview-header">
                  <div>
                    <div className="font-medium">{t('landingDesigners.hero.previewStudio')}</div>
                    <div className="kmin-preview-sub">{designersHero.previewSub}</div>
                  </div>
                  <div className="kmin-preview-status">{t('landingDesigners.hero.online')}</div>
                </div>
                <div className="kmin-preview-prompt">{designersHero.previewPrompt}</div>
                <div className="kmin-preview-grid2">
                  {DESIGNERS_HERO_PREVIEW.large.map(i => (
                    <div
                      key={i}
                      className="kmin-preview-box"
                      onClick={() => setPreviewUrl(libImgs[i])}
                    >
                      <img
                        src={libImgs[i]}
                        alt={t('landingDesigners.examples.clientVisualAlt')}
                        className="kmin-preview-img"
                        fetchPriority={i === DESIGNERS_HERO_PREVIEW.large[0] ? 'high' : undefined}
                        loading={i === DESIGNERS_HERO_PREVIEW.large[0] ? undefined : 'lazy'}
                      />
                    </div>
                  ))}
                </div>
                <div className="kmin-preview-grid3">
                  {DESIGNERS_HERO_PREVIEW.small.map(i => (
                    <div
                      key={i}
                      className="kmin-preview-box-sm"
                      onClick={() => setPreviewUrl(libImgs[i])}
                    >
                      <img
                        src={libImgs[i]}
                        alt={t('landingDesigners.examples.visualAlt')}
                        className="kmin-preview-img-sm"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="kmin-logos">
        <div className="kmin-container">
          <div className="kmin-logos-inner kmin-logos-inner--tagline">
            <span>{designersLogosStrip}</span>
          </div>
        </div>
      </section>

      <section id="benefits" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">{t('landingDesigners.audience.label')}</div>
            <h2 className="kmin-section-title">
              {t('landingDesigners.audience.title')} <span className="block text-white/50">{t('landingDesigners.audience.titleSub')}</span>
            </h2>
          </div>
          <div className="kmin-audience-grid">
            {designersAudience.map(item => (
              <div key={item.title} className="kmin-audience-card">
                <h3 className="kmin-feature-title">{item.title}</h3>
                <p className="kmin-feature-desc">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="kmin-section-header" style={{ marginTop: 64 }}>
            <div className="kmin-label">{t('landingDesigners.wholesale.label')}</div>
            <h3 className="kmin-section-title" style={{ fontSize: 'clamp(28px, 3vw, 40px)' }}>
              {t('landingDesigners.wholesale.title')}
            </h3>
          </div>
          <div className="kmin-features-grid">
            {designersWholesale.map(item => (
              <div key={item.title} className="kmin-feature-card kmin-feature-card--compact">
                <div className="kmin-feature-body">
                  <h4 className="kmin-feature-title">{item.title}</h4>
                  <p className="kmin-feature-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="tools" className="kmin-section kmin-section-alt">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">{t('landingDesigners.tools.label')}</div>
            <h2 className="kmin-section-title">
              {t('landingDesigners.tools.title')} <span className="block text-white/50">{t('landingDesigners.tools.titleSub')}</span>
            </h2>
          </div>
          <div className="kmin-features-grid">
            {designersFeatures.map((item, fi) => (
              <div key={item.title} className="kmin-feature-card">
                <div
                  className="kmin-feature-img"
                  onClick={() => setPreviewUrl(libImgs[5 + fi])}
                >
                  <img
                    src={libImgs[5 + fi]}
                    alt={t('landingDesigners.tools.exampleAlt', { title: item.title })}
                    className="kmin-preview-img"
                    loading="lazy"
                  />
                </div>
                <div className="kmin-feature-body">
                  <h4 className="kmin-feature-title">{item.title}</h4>
                  <p className="kmin-feature-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="kmin-examples-header" style={{ marginTop: 80, marginBottom: 48 }}>
            <div>
              <div className="kmin-label">{t('landingDesigners.examples.label')}</div>
              <h3 className="kmin-section-title" style={{ fontSize: 'clamp(28px, 3vw, 40px)' }}>
                {t('landingDesigners.examples.title')} <span className="block text-white/50">{t('landingDesigners.examples.titleSub')}</span>
              </h3>
            </div>
          </div>
          <div className="kmin-examples-grid">
            {Array.from({ length: LANDING_EXAMPLE_COUNT }, (_, ei) => {
              const meta = designersExamples[ei]
              const media = LANDING_EXAMPLES[ei]
              return (
                <div key={meta.title} className="kmin-example-card">
                  <div className="kmin-example-img">
                    {media.kind === 'video' ? (
                      <video
                        src={media.src}
                        poster={media.poster}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="kmin-preview-img"
                        onClick={() => setPreviewUrl(media.src)}
                      />
                    ) : (
                      <img
                        src={media.src}
                        alt={meta.title}
                        className="kmin-preview-img"
                        loading="lazy"
                        onClick={() => setPreviewUrl(media.src)}
                      />
                    )}
                  </div>
                  <div className="kmin-example-body">
                    <div className="kmin-example-meta">
                      <span>AI Generated</span>
                      <span className="text-emerald-400">PRO</span>
                    </div>
                    <h4 className="kmin-feature-title">{meta.title}</h4>
                    <p className="kmin-example-desc">{meta.desc}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <section id="how" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">{t('landingDesigners.steps.label')}</div>
            <h2 className="kmin-section-title">{t('landingDesigners.steps.title')}</h2>
          </div>
          <div className="kmin-steps-grid">
            {designersSteps.map(item => (
              <div key={item.step} className="kmin-step-card">
                <div className="kmin-step-num">{item.step}</div>
                <h3 className="kmin-feature-title">{item.title}</h3>
                <p className="kmin-feature-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="kmin-section kmin-section-alt">
        <div className="kmin-container">
          <div className="kmin-contact-grid">
            <div>
              <div className="kmin-label" style={{ justifyContent: 'flex-start' }}>
                {t('landingDesigners.contact.label')}
              </div>
              <h2 className="kmin-section-title" style={{ textAlign: 'left' }}>
                {designersContact.title}
              </h2>
              <ul className="kmin-contact-list">
                {designersContact.bullets.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <a
                href={`mailto:${DESIGNERS_CONTACT_EMAIL}`}
                className="kmin-btn-outline kmin-btn-lg"
                style={{ marginTop: 24 }}
                onClick={() => trackLandingCta('designers', 'contacts_email')}
              >
                {t('landing.contacts.emailLabel')}: {DESIGNERS_CONTACT_EMAIL}
              </a>
            </div>

            <div className="kmin-lead-form-wrap">
              {formSent ? (
                <div className="kmin-lead-success">
                  <span className="material-symbols-outlined">check_circle</span>
                  <p>{designersContact.formSuccess}</p>
                  <button
                    type="button"
                    className="kmin-btn-outline kmin-btn-block"
                    onClick={() => goRegister('contact_after_form')}
                  >
                    {designersHero.secondaryCta}
                  </button>
                </div>
              ) : (
                <form className="kmin-lead-form" onSubmit={handleLeadSubmit}>
                  <label className="kmin-lead-label">
                    {t('landingDesigners.contact.formName')}
                    <input
                      className="kmin-lead-input"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      required
                      autoComplete="name"
                    />
                  </label>
                  <label className="kmin-lead-label">
                    {t('landingDesigners.contact.formContact')}
                    <input
                      className="kmin-lead-input"
                      value={form.contact}
                      onChange={e => setForm(f => ({ ...f, contact: e.target.value }))}
                      required
                      autoComplete="email"
                    />
                  </label>
                  <label className="kmin-lead-label">
                    {t('landingDesigners.contact.formType')}
                    <select
                      className="kmin-lead-input"
                      value={form.type}
                      onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                    >
                      {designersLeadTypes.map(item => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="kmin-lead-label">
                    {t('landingDesigners.contact.formComment')}
                    <textarea
                      className="kmin-lead-input kmin-lead-textarea"
                      rows={3}
                      value={form.comment}
                      onChange={e => setForm(f => ({ ...f, comment: e.target.value }))}
                      placeholder={t('landingDesigners.contact.formCommentPlaceholder')}
                    />
                  </label>
                  <button type="submit" className="kmin-btn-primary kmin-btn-block">
                    {t('landingDesigners.contact.formSubmit')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-cta-card">
            <div className="kmin-cta-glow" />
            <div className="kmin-cta-content">
              <h3 className="kmin-cta-title">{designersFinalCta.title}</h3>
              <p className="kmin-cta-desc">{designersFinalCta.desc}</p>
              <button
                type="button"
                className="kmin-btn-primary kmin-btn-cta"
                onClick={() => goRegister('final_cta')}
              >
                {designersFinalCta.button}
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="kmin-footer">
        <div className="kmin-container">
          <div className="kmin-footer-inner">
            <div>
              <img className="kmin-logo-img kmin-logo-img--footer" src={PRODUCT_LOGO_SRC} alt={PRODUCT_NAME} style={{ margin: 0 }} />
              <div className="kmin-footer-sub">{t('landingDesigners.footer.sub')}</div>
              <div className="kmin-footer-legal">
                <div>{t('landing.footer.legalName')}</div>
                <div>{t('landing.footer.inn')}</div>
                <div>{t('landing.footer.ogrnip')}</div>
              </div>
            </div>
            <div className="kmin-footer-links">
              <Link to="/" className="kmin-footer-link-btn">
                {t('landingDesigners.footer.businessSite')}
              </Link>
              <Link to="/public-offer" className="kmin-footer-link-btn">
                {t('landing.footer.terms')}
              </Link>
              <Link to="/privacy" className="kmin-footer-link-btn">
                {t('landing.footer.privacy')}
              </Link>
              <Link to="/cookie-policy" className="kmin-footer-link-btn">
                {t('landing.footer.cookies')}
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {previewUrl && (
        <div className="kmin-preview-overlay" onClick={() => setPreviewUrl(null)}>
          <button
            type="button"
            className="kmin-preview-close"
            onClick={() => setPreviewUrl(null)}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
          {previewUrl.endsWith('.mp4') ? (
            <video
              src={previewUrl}
              autoPlay
              loop
              playsInline
              className="kmin-preview-full"
              controls
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <img
              src={previewUrl}
              alt={t('landingDesigners.preview.enlargedAlt')}
              className="kmin-preview-full"
            />
          )}
        </div>
      )}
    </div>
  )
}
